import { Loader2, Trophy } from 'lucide-react';

import { PointEntryCard } from '@apps/web/components/caregiver_points/point_entry_card';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { type CaregiverPointEntriesResponse } from '@libraries/typescript/api_client';

type CaregiverPointsTabProps = {
    readonly data: CaregiverPointEntriesResponse | undefined;
    readonly isLoading: boolean;
    readonly timezone: string;
};

export function CaregiverPointsTab({
    data,
    isLoading,
    timezone,
}: CaregiverPointsTabProps) {
    if (isLoading && !data) {
        return (
            <div className="text-muted-foreground flex h-32 items-center justify-center">
                <Loader2 className="size-5 animate-spin" />
            </div>
        );
    }

    const totalPoints = data?.total_points ?? 0;
    const entries = data?.entries ?? [];
    const lastEarnedLabel = data?.last_earned_at
        ? formatDateAndTime(data.last_earned_at, timezone)
        : null;

    return (
        <div className="space-y-4">
            <div className="rounded-md border p-4">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <div className="text-muted-foreground text-sm">
                            Total points
                        </div>
                        <div className="text-foreground mt-1 text-2xl font-semibold">
                            {totalPoints.toLocaleString()}
                        </div>
                    </div>
                    <div className="flex size-10 items-center justify-center rounded-md bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
                        <Trophy className="size-5" />
                    </div>
                </div>
                <div className="text-muted-foreground mt-3 text-sm">
                    {lastEarnedLabel
                        ? `Last earned ${lastEarnedLabel}`
                        : 'No point entries yet.'}
                </div>
            </div>
            {entries.length > 0 ? (
                <div className="space-y-2">
                    {entries.map((entry) => (
                        <PointEntryCard
                            key={entry.id}
                            entry={entry}
                            timezone={timezone}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}
